import { ApplicantType } from '@/helpers/const';
import { resolveMarriedPartnerId } from '@/helpers/resolveMarriedPartnerId';
import { useDeclarationProcess } from '@/helpers/useDeclarationProcess';
import type { ApplicantWithRelationships, ApplicantId } from '@/types';
import { computed } from 'vue';

/**
 * Composable for managing applicant permissions in the declaration flow.
 * Handles visibility and editing rules for Income, Expenses, and Assets steps.
 */
export function useApplicantPermissions() {
  const { authorisedUser, loanApp, submittedFinancials } = useDeclarationProcess();

  const currentUserId = computed(() => authorisedUser.id as ApplicantId);
  const isBrokerUser = computed(() => loanApp.value.userType === 'Broker');

  /**
   * Equity Release secondary declarations show only the signed-in applicant.
   * Bridging still shares a married or de facto partner's financials.
   */
  const isErhlOwnDetailsOnly = computed(() => !isBrokerUser.value && loanApp.value.lendingProduct === 'erhl');

  /**
   * Find the partner of a given applicant using applicantRelationships.
   * Returns the partner if they have a married_or_defacto relationship on this loan.
   */
  const findPartner = (applicantId: ApplicantId): ApplicantWithRelationships | undefined => {
    const partnerId = resolveMarriedPartnerId(applicantId, loanApp.value.applicants);
    return partnerId ? loanApp.value.applicants.find((a) => a.id === partnerId) : undefined;
  };

  const currentUserPartner = computed(() => findPartner(currentUserId.value));

  // ============================================================================
  // SUBMISSION STATUS
  // ============================================================================
  // An applicant who has submitted (signed) their declaration has submitted ALL of
  // their sections, so every section is locked — this is the authoritative signal and
  // correctly covers sections submitted empty (e.g. no assets/liabilities), which the
  // financial-content checks below cannot detect. The content checks (read from the
  // entry-time snapshot, not the live editable financials) remain so a first-time
  // submitter can still edit within their session before the declaration is signed.

  const hasDeclared = (applicantId: ApplicantId): boolean =>
    (loanApp.value.declaredApplicantIds ?? []).includes(applicantId);

  const hasSubmittedIncome = (applicantId: ApplicantId): boolean =>
    hasDeclared(applicantId) || (submittedFinancials.value[applicantId]?.income?.employment?.length ?? 0) > 0;

  /**
   * Check if an applicant has already submitted their expenses.
   */
  const hasSubmittedExpenses = (applicantId: ApplicantId): boolean =>
    hasDeclared(applicantId) || submittedFinancials.value[applicantId]?.expenses?.numberOfDependants !== undefined;

  const hasSubmittedAssets = (applicantId: ApplicantId): boolean =>
    hasDeclared(applicantId) ||
    (submittedFinancials.value[applicantId]?.assets?.length ?? 0) > 0 ||
    (submittedFinancials.value[applicantId]?.liabilities?.length ?? 0) > 0;

  // ============================================================================
  // INCOME PERMISSIONS
  // ============================================================================

  /**
   * Get list of applicants visible for the Income step.
   * - Broker: sees all applicants
   * - Single / married to non-applicant: sees own only
   * - Married to another applicant: sees own + partner's
   */
  const getVisibleApplicantsForIncome = computed((): ApplicantId[] => {
    if (isBrokerUser.value) {
      return loanApp.value.applicants.map((a) => a.id);
    }

    const visibleIds: ApplicantId[] = [currentUserId.value];
    if (currentUserPartner.value && !isErhlOwnDetailsOnly.value) {
      visibleIds.push(currentUserPartner.value.id);
    }

    return visibleIds;
  });

  /**
   * Check if current user can edit a specific applicant's income.
   * - Broker: can edit all
   * - Already-submitted income is view-only (locked)
   * - Can edit own income and partner's income (either direction)
   * - Everyone else (not self, not partner) is read-only
   */
  const canEditApplicantIncome = (applicantId: ApplicantId): boolean => {
    if (isBrokerUser.value) return true;
    if (isErhlOwnDetailsOnly.value) {
      return applicantId === currentUserId.value && !hasDeclared(applicantId);
    }
    if (hasSubmittedIncome(applicantId)) return false;
    if (applicantId === currentUserId.value) return true;
    if (!isErhlOwnDetailsOnly.value && currentUserPartner.value?.id === applicantId) return true;
    return false;
  };

  /**
   * Get completion status for a specific applicant's income.
   */
  const getApplicantIncomeStatus = (applicantId: ApplicantId) => {
    const applicant = loanApp.value.applicants.find((a) => a.id === applicantId);
    return {
      id: applicantId,
      name: applicant?.preferredName || applicant?.firstName || '',
      isCompleted: (loanApp.value.financials[applicantId]?.income?.employment?.length ?? 0) > 0,
    };
  };

  // ============================================================================
  // EXPENSES PERMISSIONS (Household-based)
  // ============================================================================

  /**
   * Whether an applicant's record actually holds the household's expenses.
   *
   * This is the signal that decides ownership, and it is deliberately narrower than
   * `hasSubmittedExpenses`: that one also returns true once an applicant has declared, and a
   * declaration does not move the expense record. Choosing an owner on declaration state switches
   * ownership to a partner holding nothing, so the UI reads an empty record, reports the household
   * incomplete, and saves a second one.
   */
  const holdsExpenseRecord = (applicantId: ApplicantId): boolean =>
    submittedFinancials.value[applicantId]?.expenses?.numberOfDependants !== undefined;

  /**
   * Get the household owner for expenses: whichever member already holds the expense record, else
   * the conventional owner (the primary applicant of the pair, else loan order). Independent of who
   * is logged in, so both partners resolve the same owner and cannot create two records.
   */
  const getHouseholdExpenseOwnerId = computed((): ApplicantId => {
    const partnerId = currentUserPartner.value?.id;
    if (!partnerId || isErhlOwnDetailsOnly.value) return currentUserId.value;

    // Loan order, so the pair resolves identically for either member.
    const memberIds = loanApp.value.applicants
      .map((a) => a.id)
      .filter((id) => id === currentUserId.value || id === partnerId);

    const holder = memberIds.find((id) => holdsExpenseRecord(id));
    if (holder) return holder;

    const primaryApplicant = loanApp.value.applicants.find((a) => a.type === ApplicantType.PRIMARY);
    if (primaryApplicant && memberIds.includes(primaryApplicant.id)) return primaryApplicant.id;

    return memberIds[0] ?? currentUserId.value;
  });

  /**
   * Check if current user can edit household expenses.
   * - Broker: can always edit
   * - Equity Release: editable until this applicant submits the declaration
   * - Already-submitted household expenses are view-only (locked). This covers both
   *   a single applicant re-opening their own submitted expenses and a partner whose
   *   shared household expenses were already submitted by the other applicant.
   * - Otherwise the current user (first submitter) can edit.
   */
  const canEditHouseholdExpenses = computed((): boolean => {
    if (isBrokerUser.value) return true;
    // Equity Release keeps each applicant's expenses editable until they submit the declaration.
    // A stored dependants count is not a submission.
    if (isErhlOwnDetailsOnly.value) return !hasDeclared(currentUserId.value);
    return !hasSubmittedExpenses(getHouseholdExpenseOwnerId.value);
  });

  /**
   * Get the applicant IDs for expenses display.
   * - Broker: sees one entry per household
   * - Regular users: see the household owner
   */
  const getHouseholdApplicantForExpenses = computed((): ApplicantId[] => {
    if (isBrokerUser.value) {
      const seen = new Set<ApplicantId>();
      const householdIds: ApplicantId[] = [];

      for (const applicant of loanApp.value.applicants) {
        if (seen.has(applicant.id)) continue;

        const partner = findPartner(applicant.id);
        if (partner) {
          const primaryOfPair =
            applicant.type === ApplicantType.PRIMARY
              ? applicant
              : partner.type === ApplicantType.PRIMARY
              ? partner
              : applicant;
          if (!seen.has(primaryOfPair.id)) {
            householdIds.push(primaryOfPair.id);
            seen.add(primaryOfPair.id);
            seen.add(partner.id);
          }
        } else {
          householdIds.push(applicant.id);
          seen.add(applicant.id);
        }
      }
      return householdIds;
    }

    return [getHouseholdExpenseOwnerId.value];
  });

  /**
   * Check if current user is viewing partner's submitted expenses (view-only mode).
   */
  const isViewingPartnerExpenses = computed((): boolean => {
    if (!currentUserPartner.value) return false;
    return hasSubmittedExpenses(currentUserPartner.value.id) && !canEditHouseholdExpenses.value;
  });

  // ============================================================================
  // ASSETS & LIABILITIES PERMISSIONS
  // ============================================================================

  /**
   * Get list of applicants to display in Assets step.
   * - Broker: sees all applicants
   * - Single / married to non-applicant: sees own only
   * - Married to another applicant: sees own + partner's (partner's card without edit button)
   */
  const getApplicantsForAssetsDisplay = computed((): ApplicantId[] => {
    if (isBrokerUser.value) {
      return loanApp.value.applicants.map((a) => a.id);
    }

    const visibleIds: ApplicantId[] = [currentUserId.value];
    if (currentUserPartner.value && !isErhlOwnDetailsOnly.value) {
      visibleIds.push(currentUserPartner.value.id);
    }

    return visibleIds;
  });

  /**
   * Check if current user can edit a specific applicant's assets.
   * - Broker: can edit all
   * - Already-submitted assets/liabilities are view-only (locked)
   * - Can edit own and partner's assets/liabilities (either direction)
   * - Everyone else (not self, not partner) is read-only
   */
  const canEditApplicantAssets = (applicantId: ApplicantId): boolean => {
    if (isBrokerUser.value) return true;
    if (isErhlOwnDetailsOnly.value) {
      return applicantId === currentUserId.value && !hasDeclared(applicantId);
    }
    if (hasSubmittedAssets(applicantId)) return false;
    if (applicantId === currentUserId.value) return true;
    if (!isErhlOwnDetailsOnly.value && currentUserPartner.value?.id === applicantId) return true;
    return false;
  };

  /**
   * Check if current user can view a specific applicant's assets data.
   * Users can see their own data and their partner's data (read-only).
   */
  const canViewApplicantAssetsData = (applicantId: ApplicantId): boolean => {
    if (isBrokerUser.value) return true;
    if (applicantId === currentUserId.value) return true;
    if (!isErhlOwnDetailsOnly.value && currentUserPartner.value?.id === applicantId) return true;
    return false;
  };

  return {
    isBrokerUser,
    currentUserPartner,
    // Income
    getVisibleApplicantsForIncome,
    canEditApplicantIncome,
    getApplicantIncomeStatus,
    // Expenses
    getHouseholdExpenseOwnerId,
    canEditHouseholdExpenses,
    getHouseholdApplicantForExpenses,
    isViewingPartnerExpenses,
    // Assets
    getApplicantsForAssetsDisplay,
    canEditApplicantAssets,
    canViewApplicantAssetsData,
  };
}
